import type { IncomingCycleRecord, IncomingHealthMetric } from './HealthDataProvider';

export type RawHealthSample = {
  type: 'weight' | 'menstrual_flow';
  value: number;
  unit: 'kg' | 'category';
  startDate: string;
  endDate?: string;
  source: 'apple_health';
  sourceId: string;
};

const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const flows: Record<number, IncomingCycleRecord['flow']> = { 2: 'light', 3: 'medium', 4: 'heavy', 5: 'none' };
const isoInstant = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,3})?Z$/;
function canonicalDate(value: unknown): string {
  if (typeof value !== 'string' || !isoInstant.test(value) || !Number.isFinite(Date.parse(value))) throw new Error('Invalid ISO instant');
  const canonical = new Date(value).toISOString();
  // Reject JS rollover of invalid calendar dates such as February 30.
  if (canonical.slice(0, 19) !== value.slice(0, 19)) throw new Error('Invalid calendar date');
  return canonical;
}

// Pure preparation for a future authenticated receiver. This sends/stores nothing.
// authenticatedUserId must come from server authentication, never the request body.
export function normalizeHealthSamples(samples: unknown[], authenticatedUserId: string, timeZone: string) {
  if (!authenticatedUserId.trim()) throw new Error('An authenticated user is required');
  const dateFormatter = new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' });
  const raw = new Map<string, RawHealthSample>();
  for (const input of samples) {
    if (!input || typeof input !== 'object') throw new Error('Invalid health sample');
    const item = input as Record<string, unknown>;
    if ((item.isSynthetic !== undefined && item.isSynthetic !== false) || item.source !== 'apple_health') throw new Error('Only real Apple Health samples are accepted');
    if (item.type !== 'weight' && item.type !== 'menstrual_flow') throw new Error('Unsupported sample type');
    if (typeof item.sourceId !== 'string' || !uuid.test(item.sourceId)) throw new Error('HealthKit UUID is required');
    if (typeof item.value !== 'number' || !Number.isFinite(item.value)) throw new Error('Invalid sample value');
    const startDate = canonicalDate(item.startDate);
    const endDate = item.endDate === undefined ? undefined : canonicalDate(item.endDate);
    if (endDate && endDate < startDate) throw new Error('Invalid end date');
    if (item.type === 'weight' && (item.unit !== 'kg' || item.value <= 0)) throw new Error('Weight must be positive kilograms');
    if (item.type === 'menstrual_flow' && (item.unit !== 'category' || !Number.isInteger(item.value) || item.value < 1 || item.value > 5)) throw new Error('Invalid menstrual flow category');
    const sample: RawHealthSample = {
      type: item.type, value: item.value, unit: item.unit as RawHealthSample['unit'],
      startDate, source: 'apple_health', sourceId: item.sourceId.toLowerCase(),
      ...(endDate ? { endDate } : {}),
    };
    const key = `${authenticatedUserId}:apple_health:${sample.sourceId}`;
    const existing = raw.get(key);
    if (existing && JSON.stringify(existing) !== JSON.stringify(sample)) throw new Error('Conflicting records for one HealthKit UUID');
    raw.set(key, sample);
  }

  const metrics: IncomingHealthMetric[] = [];
  const cycles: IncomingCycleRecord[] = [];
  for (const sample of raw.values()) {
    if (sample.type === 'weight') {
      metrics.push({ userId: authenticatedUserId, type: 'weight', value: sample.value, unit: 'kg',
        startDate: sample.startDate, endDate: sample.endDate, source: 'apple_health', sourceId: sample.sourceId });
    } else {
      cycles.push({ userId: authenticatedUserId, date: dateFormatter.format(new Date(sample.startDate)),
        source: 'apple_health', sourceId: sample.sourceId, isEstimated: false,
        ...(flows[sample.value] ? { flow: flows[sample.value] } : {}) });
    }
  }
  return { raw: [...raw.values()], metrics, cycles };
}
